import { useId, useState, type KeyboardEvent, type ReactElement, type RefObject } from 'react';
import type { UseMutationResult } from '@tanstack/react-query';
import { LIMITS, type Note } from '@chatapp-inbox/shared';
import { Button } from './Button';
import { CharacterCounter, showsCounter } from './CharacterCounter';
import { useNoteDraft } from './use-note-draft';
import styles from './NoteComposer.module.css';

interface NoteComposerProps {
  conversationId: string;
  /** Criação da nota; o estado (envio, erro) é da seção, que mostra o erro mesmo sem o formulário. */
  create: UseMutationResult<Note, Error, string>;
  /** A nota foi criada (a seção anuncia o resultado e traz a nota para a área visível). */
  onCreated: (note: Note) => void;
  /** Um envio começou: a seção limpa o aviso do resultado anterior. */
  onSubmitStart: () => void;
  /** A seção precisa do campo para devolver o foco a ele (por exemplo, depois de apagar a última nota). */
  textareaRef: RefObject<HTMLTextAreaElement | null>;
}

/**
 * Formulário da nota nova. O rascunho é guardado por conversa no cache do QueryClient (ver
 * `use-note-draft`): trocar de conversa e voltar não perde o texto, e sair da conta o descarta.
 * Ctrl/Cmd+Enter envia; Enter sozinho quebra a linha.
 */
export function NoteComposer({
  conversationId,
  create,
  onCreated,
  onSubmitStart,
  textareaRef,
}: NoteComposerProps): ReactElement {
  const uid = useId();
  const { initial, save } = useNoteDraft(conversationId);
  const [text, setText] = useState(initial);

  const trimmed = text.trim(); // o servidor também confere o texto já sem as pontas
  const over = trimmed.length > LIMITS.noteText;
  const canSend = trimmed.length > 0 && !over;
  const pending = create.isPending;

  const submit = (): void => {
    if (!canSend || pending) return;
    onSubmitStart();
    create.mutate(trimmed, {
      onSuccess: (note) => {
        setText('');
        onCreated(note);
        // O botão acabou de ficar desabilitado (campo vazio): o foco volta para escrever a próxima.
        textareaRef.current?.focus();
      },
    });
  };

  const onKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>): void => {
    // `isComposing`: o Enter que confirma uma palavra de IME (japonês, chinês) não envia nada.
    if (
      event.key === 'Enter' &&
      (event.ctrlKey || event.metaKey) &&
      !event.nativeEvent.isComposing
    ) {
      event.preventDefault();
      submit();
    }
  };

  const hintId = `${uid}-hint`;
  const counterId = `${uid}-counter`;
  const describedBy = [hintId, showsCounter(trimmed.length, LIMITS.noteText) && counterId]
    .filter(Boolean)
    .join(' ');

  return (
    <form
      className={styles.form}
      onSubmit={(event) => {
        event.preventDefault();
        submit();
      }}
    >
      <label htmlFor={`${uid}-text`} className={styles.label}>
        Nova nota
      </label>
      <textarea
        id={`${uid}-text`}
        ref={textareaRef}
        dir="auto"
        rows={2}
        className={styles.textarea}
        value={text}
        // Durante o envio o campo só fica travado para escrita (não `disabled`): o foco não se perde
        // e o texto digitado não some nem é trocado por outro no meio do envio.
        readOnly={pending}
        aria-invalid={over || undefined}
        aria-describedby={describedBy}
        onKeyDown={onKeyDown}
        onChange={(event) => {
          setText(event.target.value);
          save(event.target.value);
          if (create.isError) create.reset(); // o aviso de erro era sobre o texto de antes
        }}
      />
      <CharacterCounter id={counterId} length={trimmed.length} limit={LIMITS.noteText} />
      <div className={styles.footer}>
        <p id={hintId} className={styles.hint}>
          Atalho: Ctrl+Enter ou ⌘+Enter
        </p>
        <Button type="submit" variant="primary" busy={pending} disabled={!canSend}>
          Adicionar nota
        </Button>
      </div>
    </form>
  );
}
